import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Copy } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/whatsapp";
import { useToast } from "@/hooks/use-toast";

/**
 * O recibo do colaborador, pronto para mandar.
 *
 * Mesmo formato do "Compartilhar Plano do Campo": TEXTO puro numa caixa,
 * Copiar e WhatsApp. Nada de PDF nem imagem — o recibo precisa ser lido dentro
 * da bolha da conversa, no celular do trabalhador, sem baixar nada. E
 * `funcionarios` não guarda telefone, então o link é `wa.me/?text=` (sem
 * destinatário): quem escolhe o contato é quem envia.
 *
 * ⚠️ O texto é montado em `shared/equipe-recibo.ts`, com os MESMOS números da
 * tela. Este componente não formata dinheiro nem soma nada.
 */
export function ReciboDialog({
  open, onClose, titulo, texto, aviso,
}: {
  open: boolean;
  onClose: () => void;
  titulo: string;
  texto: string;
  /** Uma linha de contexto quando o documento ainda não é um pagamento feito. */
  aviso?: string;
}) {
  const { toast } = useToast();

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      toast({ title: "Copiado!", description: "O recibo está na área de transferência." });
    } catch {
      // Clipboard bloqueado (contexto sem permissão): o texto continua na tela
      // para seleção manual — nunca falhar em silêncio.
      toast({ title: "Não consegui copiar", description: "Selecione o texto e copie à mão.", variant: "destructive" });
    }
  };

  const whatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={aberto => { if (!aberto) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {aviso && <DialogDescription>{aviso}</DialogDescription>}
        </DialogHeader>
        <Textarea
          value={texto}
          readOnly
          rows={16}
          className="font-mono text-xs leading-relaxed"
          data-testid="recibo-texto"
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copiar} className="min-h-11 sm:min-h-0">
            <Copy className="h-4 w-4 mr-2" />
            Copiar
          </Button>
          <Button onClick={whatsapp} className="min-h-11 sm:min-h-0" data-testid="recibo-whatsapp">
            <WhatsAppIcon className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
